import React, { useState, useEffect } from "react";
import {
  Row,
  Card,
  CardBody,
  Table,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import axios from "axios";
import * as XLSX from "xlsx";
import { END_POINT } from "../../../constants/defaultValues";
import { NotificationManager } from "../../../components/common/react-notifications";
import { Colxx, Separator } from "../../../components/common/CustomBootstrap";

const DEFAULT_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 320 240'%3E%3Crect fill='%23f0f0f0' width='320' height='240'/%3E%3Cpath fill='%23ccc' d='M160 80c-22 0-40 18-40 40s18 40 40 40 40-18 40-40-18-40-40-40zm0 64c-13.3 0-24-10.7-24-24s10.7-24 24-24 24 10.7 24 24-10.7 24-24 24z'/%3E%3Cpath fill='%23ccc' d='M240 180H80l40-50 20 25 40-50 60 75z'/%3E%3C/svg%3E";

const getImageUrl = (image) => {
  if (!image) return DEFAULT_PLACEHOLDER;
  if (image.startsWith('http') || image.startsWith('data:')) return image;
  return `${END_POINT}${image}`;
};

const spinnerDotStyle = { backgroundColor: "currentColor" };

const LoadingSpinner = ({ className = "" }) => (
  <div className={`spinner text-primary ${className}`} role="status" aria-live="polite" style={{ display: "inline-block" }}>
    <span className="bounce1" style={spinnerDotStyle} />
    <span className="bounce2" style={spinnerDotStyle} />
    <span className="bounce3" style={spinnerDotStyle} />
  </div>
);

const ProductTemplatesManager = () => {
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  // Excel Upload States
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewItems, setPreviewItems] = useState([]);
  const [defaultIndustry, setDefaultIndustry] = useState("TAP_HOA");
  const [submittingImport, setSubmittingImport] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("ALL");
  const [selectedBrandFilter, setSelectedBrandFilter] = useState("ALL");

  // Barcode Search States
  const [searchTargetId, setSearchTargetId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    fetchContributions();
  }, []);

  // Auto-reset category filter if it becomes invalid under selected brand in preview items
  useEffect(() => {
    if (selectedCategoryFilter !== "ALL" && previewItems.length > 0) {
      const isValid = previewItems.some(t => 
        t.category_name === selectedCategoryFilter && 
        (selectedBrandFilter === "ALL" || t.brand_name === selectedBrandFilter)
      );
      if (!isValid) {
        setSelectedCategoryFilter("ALL");
      }
    }
  }, [selectedBrandFilter, selectedCategoryFilter, previewItems]);

  // Auto-reset brand filter if it becomes invalid under selected category in preview items
  useEffect(() => {
    if (selectedBrandFilter !== "ALL" && previewItems.length > 0) {
      const isValid = previewItems.some(t => 
        t.brand_name === selectedBrandFilter && 
        (selectedCategoryFilter === "ALL" || t.category_name === selectedCategoryFilter)
      );
      if (!isValid) {
        setSelectedBrandFilter("ALL");
      }
    }
  }, [selectedCategoryFilter, selectedBrandFilter, previewItems]);

  const fetchContributions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${END_POINT}/product-template/contributions`);
      if (response.data && response.data.data) {
        setContributions(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching contributions:", error);
      NotificationManager.error("Không thể tải danh sách đóng góp", "Lỗi", 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setProcessingId(id);
    try {
      await axios.post(`${END_POINT}/product-template/contributions/approve/${id}`);
      NotificationManager.success("Đã duyệt và cập nhật danh mục mẫu thành công!", "Thành công", 3000);
      fetchContributions();
    } catch (error) {
      console.error("Error approving contribution:", error);
      NotificationManager.error(
        error.response?.data?.error || "Lỗi khi duyệt đóng góp",
        "Lỗi",
        3000
      );
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (id) => {
    setProcessingId(id);
    try {
      await axios.post(`${END_POINT}/product-template/contributions/reject/${id}`);
      NotificationManager.success("Đã từ chối đề xuất đóng góp mẫu", "Thành công", 3000);
      fetchContributions();
    } catch (error) {
      console.error("Error rejecting contribution:", error);
      NotificationManager.error(
        error.response?.data?.error || "Lỗi khi từ chối đóng góp",
        "Lỗi",
        3000
      );
    } finally {
      setProcessingId(null);
    }
  };

  // Excel Upload Parser
  const handleExcelUpload = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        if (rows.length < 2) {
          NotificationManager.error("File Excel phải có ít nhất 1 hàng tiêu đề và 1 hàng dữ liệu", "Lỗi", 3000);
          return;
        }

        const headers = rows[0].map(h => String(h || '').trim().toLowerCase());
        
        const nameIdx = headers.findIndex(h => h.includes('tên') || h.includes('name'));
        const barcodeIdx = headers.findIndex(h => h.includes('vạch') || h.includes('barcode') || h.includes('code'));
        const brandIdx = headers.findIndex(h => h.includes('hiệu') || h.includes('brand'));
        const unitIdx = headers.findIndex(h => h.includes('vị') || h.includes('unit'));
        const categoryIdx = headers.findIndex(h => h.includes('mục') || h.includes('category'));
        const priceIdx = headers.findIndex(h => h.includes('bán') || h.includes('price') || h.includes('suggested_price'));
        const costPriceIdx = headers.findIndex(h => h.includes('nhập') || h.includes('cost') || h.includes('suggested_cost_price'));
        const industryIdx = headers.findIndex(h => h.includes('ngành') || h.includes('industry'));

        if (nameIdx === -1) {
          NotificationManager.error("File Excel thiếu cột 'Tên sản phẩm' hoặc 'Name'", "Lỗi", 3000);
          return;
        }

        const parsedData = rows.slice(1).map((row, idx) => {
          return {
            id: idx,
            name: row[nameIdx] ? String(row[nameIdx]).trim() : '',
            barcode: barcodeIdx !== -1 && row[barcodeIdx] ? String(row[barcodeIdx]).trim() : '',
            brand_name: brandIdx !== -1 && row[brandIdx] ? String(row[brandIdx]).trim() : '',
            sale_unit: unitIdx !== -1 && row[unitIdx] ? String(row[unitIdx]).trim() : 'Cái',
            category_name: categoryIdx !== -1 && row[categoryIdx] ? String(row[categoryIdx]).trim() : 'Khác',
            suggested_price: priceIdx !== -1 && row[priceIdx] ? parseFloat(row[priceIdx]) || 0 : 0,
            suggested_cost_price: costPriceIdx !== -1 && row[costPriceIdx] ? parseFloat(row[costPriceIdx]) || 0 : 0,
            industry_type: industryIdx !== -1 && row[industryIdx] ? String(row[industryIdx]).trim() : ''
          };
        }).filter(item => item.name !== '');

        if (parsedData.length === 0) {
          NotificationManager.warning("Không tìm thấy dòng dữ liệu hợp lệ nào", "Cảnh báo", 3000);
          return;
        }

        setPreviewItems(parsedData);
        setDefaultIndustry("TAP_HOA");
        setCurrentPage(1);
        setSelectedCategoryFilter("ALL");
        setSelectedBrandFilter("ALL");
        setShowPreviewModal(true);
      } catch (err) {
        console.error("Excel parse error:", err);
        NotificationManager.error("Không thể đọc file Excel. Vui lòng kiểm tra định dạng", "Lỗi", 3000);
      }
      
      e.target.value = "";
    };

    reader.readAsArrayBuffer(file);
  };

  const handleImportSubmit = async () => {
    setSubmittingImport(true);
    try {
      const response = await axios.post(`${END_POINT}/product-template/import`, {
        data: previewItems,
        defaultIndustry: defaultIndustry
      });
      if (response.data && response.data.status === 200) {
        NotificationManager.success(response.data.message || "Import sản phẩm mẫu thành công!", "Thành công", 3000);
        setShowPreviewModal(false);
        fetchContributions();
      }
    } catch (err) {
      console.error("Import error:", err);
      NotificationManager.error(
        err.response?.data?.error || "Lỗi khi import sản phẩm mẫu",
        "Lỗi",
        3000
      );
    } finally {
      setSubmittingImport(false);
    }
  };

  // Barcode Lookup Handlers
  const handleSearchBarcodeClick = (rowId, productName) => {
    setSearchTargetId(rowId);
    setSearchQuery(productName);
    setSearchResults([]);
    triggerBarcodeSearch(productName);
  };

  const triggerBarcodeSearch = async (query) => {
    if (!query || query.trim() === "") return;
    setSearchLoading(true);
    try {
      const response = await axios.get(`${END_POINT}/product-template/search-barcode`, {
        params: { name: query }
      });
      if (response.data && response.data.data) {
        setSearchResults(response.data.data);
      }
    } catch (err) {
      console.error("Error searching barcode:", err);
      NotificationManager.error("Không thể tìm kiếm mã vạch", "Lỗi", 3000);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectSearchResult = (candidate) => {
    setPreviewItems(prev => prev.map(item => {
      if (item.id === searchTargetId) {
        return {
          ...item,
          barcode: candidate.barcode,
          brand_name: candidate.brand_name || item.brand_name,
          sale_unit: candidate.sale_unit || item.sale_unit,
          category_name: candidate.category_name || item.category_name
        };
      }
      return item;
    }));
    setSearchTargetId(null);
  };

  const formatNumber = (num) => {
    if (!num) return "0đ";
    return new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(num);
  };

  const renderComparisonField = (label, currentVal, proposedVal) => {
    const isDifferent = currentVal !== proposedVal;
    return (
      <div className="mb-1" style={{ fontSize: "0.85rem" }}>
        <span className="text-muted">{label}: </span>
        {isDifferent ? (
          <>
            <del className="text-danger mr-2">{currentVal || "Trống"}</del>
            <strong className="text-success">{proposedVal || "Trống"}</strong>
          </>
        ) : (
          <span>{proposedVal || "Trống"}</span>
        )}
      </div>
    );
  };

  return (
    <div className="dashboard-wrapper">
      <Row>
        <Colxx xxs="12">
          <div className="mb-4 d-flex justify-content-between align-items-center">
            <div>
              <h1>Duyệt Đề Xuất Đóng Góp Mẫu Sản Phẩm</h1>
              <p className="text-muted mb-0">
                Xem, so sánh và kiểm duyệt các thông tin sản phẩm mẫu do người dùng đóng góp/sửa đổi trước khi đưa vào danh mục chung.
              </p>
            </div>
            <div>
              <Button
                color="primary"
                onClick={() => document.getElementById("excel-template-upload-input").click()}
              >
                <i className="simple-icon-cloud-upload mr-2" />
                Nhập từ Excel
              </Button>
              <input
                type="file"
                id="excel-template-upload-input"
                style={{ display: "none" }}
                accept=".xlsx, .xls"
                onChange={handleExcelUpload}
              />
            </div>
          </div>
          <Separator className="mb-4" />
        </Colxx>
      </Row>

      <Row>
        <Colxx xxs="12">
          <Card>
            <CardBody>
              {loading ? (
                <div className="text-center py-5">
                  <LoadingSpinner className="mx-auto" />
                  <div className="mt-2 text-muted">Đang tải danh sách đóng góp...</div>
                </div>
              ) : contributions.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  Không có đề xuất đóng góp nào đang chờ duyệt.
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th style={{ width: "15%" }}>Người đóng góp</th>
                        <th style={{ width: "15%" }}>Mã vạch</th>
                        <th style={{ width: "50%" }}>Thông tin thay đổi</th>
                        <th style={{ width: "20%", textAlign: "center" }}>Thao tác</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contributions.map((item) => {
                        const isNew = !item.template_id;
                        const currentTemp = item.template || {};

                        return (
                          <tr key={item.id}>
                            <td>
                              <div className="font-weight-bold">{item.user?.fullname || item.user?.username || "N/A"}</div>
                              <small className="text-muted">ID: {item.user_id}</small>
                            </td>
                            <td>
                              <Badge color="secondary" className="p-2 mb-1" style={{ fontSize: "0.85rem" }}>
                                {item.barcode}
                              </Badge>
                              <div>
                                {isNew ? (
                                  <Badge color="success">Mã vạch mới</Badge>
                                ) : (
                                  <Badge color="warning">Cập nhật mẫu</Badge>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-start">
                                <img
                                  src={getImageUrl(item.image)}
                                  alt={item.name}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = DEFAULT_PLACEHOLDER;
                                  }}
                                  className="mr-3 rounded"
                                  style={{ width: "50px", height: "50px", objectFit: "cover" }}
                                />
                                <div>
                                  {isNew ? (
                                    <>
                                      <div className="font-weight-bold text-primary" style={{ fontSize: "0.95rem" }}>{item.name}</div>
                                      <div style={{ fontSize: "0.85rem" }}>Thương hiệu: {item.brand_name || "N/A"} | ĐVT: {item.sale_unit || "Cái"}</div>
                                      <div style={{ fontSize: "0.85rem" }}>Danh mục: {item.category_name || "N/A"}</div>
                                      <div style={{ fontSize: "0.85rem" }}>
                                        Giá bán gợi ý: <strong className="text-info">{formatNumber(item.suggested_price)}</strong> | 
                                        Giá nhập gợi ý: <strong className="text-secondary">{formatNumber(item.suggested_cost_price)}</strong>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      {renderComparisonField("Tên sản phẩm", currentTemp.name, item.name)}
                                      {renderComparisonField("Thương hiệu", currentTemp.brand_name, item.brand_name)}
                                      {renderComparisonField("Đơn vị tính", currentTemp.sale_unit, item.sale_unit)}
                                      {renderComparisonField("Danh mục", currentTemp.category_name, item.category_name)}
                                      {renderComparisonField(
                                        "Giá bán gợi ý",
                                        formatNumber(currentTemp.suggested_price),
                                        formatNumber(item.suggested_price)
                                      )}
                                      {renderComparisonField(
                                        "Giá nhập gợi ý",
                                        formatNumber(currentTemp.suggested_cost_price),
                                        formatNumber(item.suggested_cost_price)
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td style={{ verticalAlign: "middle", textAlign: "center" }}>
                              {processingId === item.id ? (
                                <LoadingSpinner className="mx-auto" />
                              ) : (
                                <div className="d-flex justify-content-center">
                                  <Button
                                    color="success"
                                    size="sm"
                                    className="mr-2 px-3"
                                    onClick={() => handleApprove(item.id)}
                                  >
                                    Duyệt
                                  </Button>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    className="px-3"
                                    onClick={() => handleReject(item.id)}
                                  >
                                    Từ chối
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Colxx>
      </Row>

      {/* Excel Preview Confirmation Modal */}
      <Modal isOpen={showPreviewModal} toggle={() => setShowPreviewModal(false)} size="lg" className="import-preview-modal" modalClassName="import-preview-modal-wrapper" backdrop={false} autoFocus={false}>
        <style>{`
          .import-preview-modal-wrapper {
            background-color: rgba(0, 0, 0, 0.5) !important;
          }
          .import-preview-modal.modal-dialog,
          .import-preview-modal .modal-dialog {
            max-width: 1200px !important;
            width: 95vw !important;
          }
          .import-preview-modal .modal-content {
            border-radius: 12px;
            border: none;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          }
          .preview-kpi-card {
            border-radius: 8px;
            background: #ffffff;
            border: 1px solid #eaeaea;
            transition: transform 0.2s, box-shadow 0.2s;
          }
          .preview-kpi-card:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.04);
          }
          .preview-table th {
            font-size: 0.75rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #606060;
            background: #f8f9fa;
            border-bottom: 2px solid #eaeaea;
            padding: 12px 10px;
          }
          .preview-table td {
            padding: 14px 10px;
            vertical-align: middle !important;
            border-bottom: 1px solid #f2f4f5;
          }
          .preview-table tr {
            transition: background-color 0.15s ease;
          }
          .preview-table tr:hover {
            background-color: #fafbfc !important;
          }
          .badge-industry-cafe { background-color: #efebe9; color: #5d4037; font-weight: 600; border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; display: inline-block; }
          .badge-industry-restaurant { background-color: #fff3e0; color: #e65100; font-weight: 600; border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; display: inline-block; }
          .badge-industry-retail { background-color: #e0f2f1; color: #004d40; font-weight: 600; border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; display: inline-block; }
          .badge-industry-fashion { background-color: #fce4ec; color: #880e4f; font-weight: 600; border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; display: inline-block; }
          .badge-industry-cosmetic { background-color: #e8eaf6; color: #1a237e; font-weight: 600; border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; display: inline-block; }
          .badge-industry-tap_hoa { background-color: #fff8e1; color: #ff6f00; font-weight: 600; border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; display: inline-block; }
          .badge-industry-default { background-color: #eceff1; color: #37474f; font-weight: 600; border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; display: inline-block; }
          
          .row-warning {
            background-color: rgba(255, 193, 7, 0.015);
          }
          .barcode-warning-cell {
            background: rgba(255, 193, 7, 0.08);
            border-radius: 6px;
            padding: 4px 8px;
            display: inline-flex;
            align-items: center;
            color: #d87f00;
            font-weight: 600;
            font-size: 0.75rem;
          }
          .pulse-magnifier {
            animation: pulse-border 2s infinite;
          }
          @keyframes pulse-border {
            0% { box-shadow: 0 0 0 0 rgba(0, 123, 255, 0.4); }
            70% { box-shadow: 0 0 0 6px rgba(0, 123, 255, 0); }
            100% { box-shadow: 0 0 0 0 rgba(0, 123, 255, 0); }
          }
        `}</style>
        <ModalHeader toggle={() => setShowPreviewModal(false)} className="bg-light border-bottom-0 py-3">
          <div className="d-flex align-items-center">
            <i className="simple-icon-grid mr-2 text-primary" style={{ fontSize: "1.4rem" }} />
            <div>
              <span className="h4 font-weight-bold mb-0" style={{ color: "#303030" }}>Xem Trước Dữ Liệu Import Từ Excel</span>
              <p className="text-muted mb-0" style={{ fontSize: "0.8rem" }}>Kiểm tra, chuẩn hóa ngành hàng và tra cứu mã vạch trực tuyến trước khi lưu</p>
            </div>
          </div>
        </ModalHeader>
        <ModalBody className="pt-2">
          {(() => {
            const availableCategories = ["ALL", ...new Set(
              previewItems
                .filter(item => selectedBrandFilter === "ALL" || item.brand_name === selectedBrandFilter)
                .map(t => t.category_name)
                .filter(Boolean)
            )];

            const availableBrands = ["ALL", ...new Set(
              previewItems
                .filter(item => selectedCategoryFilter === "ALL" || item.category_name === selectedCategoryFilter)
                .map(t => t.brand_name)
                .filter(Boolean)
            )];

            const filteredItems = previewItems.filter(item => {
              const matchesCategory = selectedCategoryFilter === "ALL" || item.category_name === selectedCategoryFilter;
              const matchesBrand = selectedBrandFilter === "ALL" || item.brand_name === selectedBrandFilter;
              return matchesCategory && matchesBrand;
            });

            const totalItems = filteredItems.length;
            const itemsWithBarcode = filteredItems.filter(item => item.barcode).length;
            const itemsMissingBarcode = totalItems - itemsWithBarcode;
            const totalPages = Math.ceil(totalItems / pageSize);
            const paginatedItems = filteredItems.slice((currentPage - 1) * pageSize, currentPage * pageSize);

            const getBadgeClass = (ind) => {
              const i = String(ind || '').trim().toLowerCase();
              if (!i) return "badge-industry-default";
              if (i === "tap_hoa") return "badge-industry-tap_hoa";
              if (i === "cafe") return "badge-industry-cafe";
              if (i === "restaurant") return "badge-industry-restaurant";
              if (i === "retail") return "badge-industry-retail";
              if (i === "fashion") return "badge-industry-fashion";
              if (i === "cosmetic") return "badge-industry-cosmetic";
              return "badge-industry-default";
            };

            return (
              <>
                <div className="p-3 mb-3 rounded" style={{ background: "#f8f9fa", border: "1px solid #eaeaea" }}>
                  <Row className="align-items-center">
                    <Colxx md="5" xxs="12">
                      <FormGroup className="mb-md-0 mb-3">
                        <Label for="default-industry-select" className="font-weight-bold mb-1" style={{ fontSize: "0.85rem", color: "#303030" }}>
                          Ngành hàng mặc định (cho dòng trống):
                        </Label>
                        <Input
                          type="select"
                          id="default-industry-select"
                          value={defaultIndustry}
                          onChange={(e) => setDefaultIndustry(e.target.value)}
                          style={{ borderRadius: "6px", height: "38px", fontSize: "0.85rem" }}
                        >
                          <option value="TAP_HOA">Tạp hóa (TAP_HOA)</option>
                          <option value="cafe">Cà phê (cafe)</option>
                          <option value="restaurant">Nhà hàng (restaurant)</option>
                          <option value="retail">Bán lẻ (retail)</option>
                          <option value="fashion">Thời trang (fashion)</option>
                          <option value="cosmetic">Mỹ phẩm (cosmetic)</option>
                        </Input>
                      </FormGroup>
                    </Colxx>
                    <Colxx md="7" xxs="12">
                      <Row className="no-gutters justify-content-end">
                        <Colxx xxs="4" className="text-center px-1">
                          <div className="preview-kpi-card p-2">
                            <div className="text-muted small" style={{ fontSize: "0.75rem" }}>Tổng số dòng</div>
                            <div className="h5 font-weight-bold text-dark mb-0">{totalItems}</div>
                          </div>
                        </Colxx>
                        <Colxx xxs="4" className="text-center px-1">
                          <div className="preview-kpi-card p-2" style={{ borderLeft: "3px solid #28a745" }}>
                            <div className="text-muted small" style={{ fontSize: "0.75rem" }}>Đầy đủ mã</div>
                            <div className="h5 font-weight-bold text-success mb-0">{itemsWithBarcode}</div>
                          </div>
                        </Colxx>
                        <Colxx xxs="4" className="text-center px-1">
                          <div className="preview-kpi-card p-2" style={{ borderLeft: "3px solid #ffc107" }}>
                            <div className="text-muted small" style={{ fontSize: "0.75rem" }}>Chưa có mã</div>
                            <div className="h5 font-weight-bold text-warning mb-0">{itemsMissingBarcode}</div>
                          </div>
                        </Colxx>
                      </Row>
                    </Colxx>
                  </Row>

                  <Row className="align-items-center mt-3">
                    <Colxx md="6" xxs="12">
                      <FormGroup className="mb-md-0 mb-3">
                        <Label for="category-filter-select" className="font-weight-bold mb-1" style={{ fontSize: "0.85rem", color: "#303030" }}>
                          Lọc theo Danh mục:
                        </Label>
                        <Input
                          type="select"
                          id="category-filter-select"
                          value={selectedCategoryFilter}
                          onChange={(e) => {
                            setSelectedCategoryFilter(e.target.value);
                            setCurrentPage(1);
                          }}
                          style={{ borderRadius: "6px", height: "38px", fontSize: "0.85rem" }}
                        >
                          {availableCategories.map(cat => (
                            <option key={cat} value={cat}>{cat === "ALL" ? "Tất cả danh mục" : cat}</option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Colxx>
                    <Colxx md="6" xxs="12">
                      <FormGroup className="mb-md-0 mb-3">
                        <Label for="brand-filter-select" className="font-weight-bold mb-1" style={{ fontSize: "0.85rem", color: "#303030" }}>
                          Lọc theo Thương hiệu:
                        </Label>
                        <Input
                          type="select"
                          id="brand-filter-select"
                          value={selectedBrandFilter}
                          onChange={(e) => {
                            setSelectedBrandFilter(e.target.value);
                            setCurrentPage(1);
                          }}
                          style={{ borderRadius: "6px", height: "38px", fontSize: "0.85rem" }}
                        >
                          {availableBrands.map(br => (
                            <option key={br} value={br}>{br === "ALL" ? "Tất cả thương hiệu" : br}</option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Colxx>
                  </Row>
                </div>

                <Row>
                  <Colxx md={searchTargetId ? "8" : "12"} xxs="12">
                    <div className="table-responsive rounded border" style={{ maxHeight: "400px", overflowY: "auto", background: "#ffffff" }}>
                      <Table className="preview-table mb-0" size="sm">
                        <thead>
                          <tr>
                            <th style={{ width: "25%", textAlign: "center", verticalAlign: "middle" }}>Tên sản phẩm</th>
                            <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Thương hiệu</th>
                            <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Danh mục</th>
                            <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Mã vạch</th>
                            <th style={{ width: "10%", textAlign: "center", verticalAlign: "middle" }}>Đơn vị</th>
                            <th style={{ width: "12%", textAlign: "center", verticalAlign: "middle" }}>Giá gợi ý (Bán/Nhập)</th>
                            <th style={{ width: "8%", textAlign: "center", verticalAlign: "middle" }}>Ngành</th>
                            <th style={{ width: "5%", textAlign: "center", verticalAlign: "middle" }}>Tìm</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedItems.map((item) => {
                            return (
                              <tr key={item.id} className={!item.barcode ? "row-warning" : ""} style={searchTargetId === item.id ? { backgroundColor: "rgba(0, 123, 255, 0.05)" } : {}}>
                                <td style={{ verticalAlign: "middle" }}>
                                  <div className="font-weight-bold text-dark" style={{ fontSize: "0.88rem" }}>{item.name}</div>
                                </td>
                                <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                  {item.brand_name ? (
                                    item.brand_name
                                  ) : (
                                    <Input
                                      type="text"
                                      size="sm"
                                      placeholder="Nhập thương hiệu"
                                      value={item.brand_name || ""}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setPreviewItems(prev => prev.map(p => p.id === item.id ? { ...p, brand_name: val } : p));
                                      }}
                                      style={{ fontSize: "0.8rem", height: "30px", textAlign: "center" }}
                                    />
                                  )}
                                </td>
                                <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                  {item.category_name ? (
                                    <span className="badge badge-light badge-pill font-weight-semibold">
                                      {item.category_name}
                                    </span>
                                  ) : (
                                    <Input
                                      type="text"
                                      size="sm"
                                      placeholder="Nhập danh mục"
                                      value={item.category_name || ""}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setPreviewItems(prev => prev.map(p => p.id === item.id ? { ...p, category_name: val } : p));
                                      }}
                                      style={{ fontSize: "0.8rem", height: "30px", textAlign: "center" }}
                                    />
                                  )}
                                </td>
                                <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                  {item.barcode ? (
                                    <Badge color="secondary" className="px-2 py-1 font-weight-semibold" style={{ fontSize: "0.75rem", borderRadius: "4px" }}>
                                      {item.barcode}
                                    </Badge>
                                  ) : (
                                    <div className="barcode-warning-cell">
                                      <i className="simple-icon-exclamation mr-1" />
                                      Chưa có mã vạch
                                    </div>
                                  )}
                                </td>
                                <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                  <div style={{ fontSize: "0.8rem", color: "#303030" }}><strong>{item.sale_unit}</strong></div>
                                </td>
                                <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                  <div style={{ fontSize: "0.8rem", color: "#004d40" }}>Bán: <strong>{formatNumber(item.suggested_price)}</strong></div>
                                  <div className="text-muted small" style={{ fontSize: "0.75rem" }}>Nhập: {formatNumber(item.suggested_cost_price)}</div>
                                </td>
                                <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                  <span className={getBadgeClass(item.industry_type)}>
                                    {item.industry_type ? item.industry_type.toUpperCase() : "MẶC ĐỊNH"}
                                  </span>
                                </td>
                                <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                  {!item.barcode && (
                                    <Button
                                      color="primary"
                                      className="p-0 rounded-circle pulse-magnifier d-inline-flex align-items-center justify-content-center"
                                      style={{ width: "28px", height: "28px", border: "none" }}
                                      onClick={() => handleSearchBarcodeClick(item.id, item.name)}
                                      title="Tìm mã vạch trực tuyến"
                                    >
                                      <i className="simple-icon-magnifier" style={{ fontSize: "0.85rem", color: "#ffffff" }} />
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>

                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3 p-2 bg-light rounded border">
                        <div className="text-muted small" style={{ fontSize: "0.75rem" }}>
                          Hiển thị dòng <strong>{(currentPage - 1) * pageSize + 1}</strong> - <strong>{Math.min(currentPage * pageSize, totalItems)}</strong> trên tổng số <strong>{totalItems}</strong>
                        </div>
                        <div className="d-flex align-items-center">
                          <Button
                            color="secondary"
                            size="xs"
                            outline
                            className="mr-1 py-0 px-2"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(1)}
                            title="Trang đầu"
                            style={{ fontSize: "0.75rem" }}
                          >
                            «
                          </Button>
                          <Button
                            color="secondary"
                            size="xs"
                            outline
                            className="mr-2 py-0 px-2"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            title="Trang trước"
                            style={{ fontSize: "0.75rem" }}
                          >
                            ‹
                          </Button>
                          <span className="mx-1 font-weight-bold" style={{ fontSize: "0.78rem" }}>
                            Trang {currentPage} / {totalPages}
                          </span>
                          <Button
                            color="secondary"
                            size="xs"
                            outline
                            className="ml-2 py-0 px-2"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            title="Trang sau"
                            style={{ fontSize: "0.75rem" }}
                          >
                            ›
                          </Button>
                          <Button
                            color="secondary"
                            size="xs"
                            outline
                            className="ml-1 py-0 px-2"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(totalPages)}
                            title="Trang cuối"
                            style={{ fontSize: "0.75rem" }}
                          >
                            »
                          </Button>
                        </div>
                      </div>
                    )}
                  </Colxx>

                  {searchTargetId && (
                    <Colxx md="4" xxs="12" className="pl-md-0 mt-3 mt-md-0">
                      <div className="p-3 border rounded bg-white shadow-sm" style={{ height: "400px", overflowY: "auto" }}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h5 className="font-weight-bold mb-0 text-primary" style={{ fontSize: "1rem" }}>
                            <i className="simple-icon-magnifier mr-1" />
                            Tra Cứu Mã Vạch
                          </h5>
                          <Button 
                            color="link" 
                            className="p-0 text-muted" 
                            onClick={() => setSearchTargetId(null)}
                            style={{ textDecoration: "none", fontSize: "1.2rem", fontWeight: "bold" }}
                          >
                            &times;
                          </Button>
                        </div>
                        
                        <div className="small text-muted mb-1">Sản phẩm đang chọn:</div>
                        <div className="font-weight-bold text-dark mb-3 p-2 bg-light rounded" style={{ fontSize: "0.8rem", lineHeight: "1.3" }}>
                          {previewItems.find(i => i.id === searchTargetId)?.name}
                        </div>

                        <div className="d-flex mb-3 p-1 rounded border bg-light">
                          <Input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Nhập tên tìm kiếm..."
                            className="border-0 bg-transparent shadow-none px-2"
                            style={{ height: "32px", fontSize: "0.8rem" }}
                            onKeyPress={(e) => e.key === "Enter" && triggerBarcodeSearch(searchQuery)}
                          />
                          <Button color="primary" size="sm" className="px-3" style={{ borderRadius: "4px" }} onClick={() => triggerBarcodeSearch(searchQuery)}>
                            Tìm
                          </Button>
                        </div>

                        {searchLoading ? (
                          <div className="text-center py-4">
                            <LoadingSpinner />
                            <div className="mt-2 text-muted small">Đang tìm trực tuyến...</div>
                          </div>
                        ) : searchResults.length === 0 ? (
                          <div className="text-center py-4 text-muted small">
                            Không tìm thấy kết quả nào.
                          </div>
                        ) : (
                          <div className="border rounded bg-white" style={{ maxHeight: "210px", overflowY: "auto" }}>
                            <Table hover size="sm" className="mb-0">
                              <tbody>
                                {searchResults.map((candidate) => (
                                  <tr key={candidate.barcode} style={{ cursor: "pointer" }} onClick={() => handleSelectSearchResult(candidate)}>
                                    <td style={{ verticalAlign: "middle", padding: "6px", width: "40px" }}>
                                      <img
                                        src={candidate.image || DEFAULT_PLACEHOLDER}
                                        alt={candidate.name}
                                        style={{ width: "30px", height: "30px", objectFit: "cover", borderRadius: "4px" }}
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = DEFAULT_PLACEHOLDER;
                                        }}
                                      />
                                    </td>
                                    <td style={{ verticalAlign: "middle", padding: "6px" }}>
                                      <div className="font-weight-bold text-dark" style={{ fontSize: "0.78rem", lineHeight: "1.2" }}>{candidate.name}</div>
                                      <div style={{ fontSize: "0.7rem", color: "#606060" }}>
                                        Mã vạch: <span className="text-primary font-weight-bold">{candidate.barcode}</span>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        )}
                      </div>
                    </Colxx>
                  )}
                </Row>
              </>
            );
          })()}
        </ModalBody>
        <ModalFooter className="border-top-0 pt-0">
          {submittingImport ? (
            <div className="d-flex align-items-center">
              <LoadingSpinner className="mr-3" />
              <span className="text-muted small">Đang ghi nhận dữ liệu vào hệ thống...</span>
            </div>
          ) : (
            <>
              <Button color="secondary" outline onClick={() => setShowPreviewModal(false)} style={{ borderRadius: "4px" }}>
                Hủy bỏ
              </Button>
              <Button color="success" className="px-4" onClick={handleImportSubmit} style={{ borderRadius: "4px" }}>
                Xác nhận Nhập ({previewItems.length} sản phẩm)
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ProductTemplatesManager;
